import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { HeroSolidBGView, type HeroSolidBGContentProps } from './HeroSolidBG';

export function GalleryHero(props: HeroSolidBGContentProps) {
  const bgDefinition = contentSchema.slotGroups!['galleryhero.bg_image'];
  const ctaDefinition = contentSchema.slotGroups!['galleryhero.cta_link'];
  return (
    <>
      <HeroSolidBGView {...props} prefix="galleryhero" bgDefinition={bgDefinition} />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent path="galleryhero.title" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableToken path="galleryhero.subtitle" type="richtext" label="Subtitle" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent path="galleryhero.blurb" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="galleryhero.bg_image" definition={bgDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="galleryhero.cta_link" definition={ctaDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryHero',
  displayName: 'Gallery Hero',
  tokens: {
    'galleryhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Gallery</p>',
    },
    'galleryhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Apartment Photo Gallery in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'galleryhero.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultField: 'property_gallery_blurb',
    },
  },
  slotGroups: {
    'galleryhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Property photo',
        },
      },
      generator: {
        prompt:
          'Pick the hero background photo for the Gallery page: a visually striking photo of this property or its community. Write alt text describing what the photo shows.',
        context:
          'Full-bleed background hero at the top of the photo Gallery page, behind a semi-opaque primary-color overlay with centered white headline text. Prefer staged or professional photos; skip blurry, dark, cluttered, or unappealing shots. Priority: relevance to the property first, image quality second, uniqueness third. Each inner page (Floor Plans, Amenities, Gallery, Virtual Tour, Neighborhood, Contact Us) has its own hero — do not reuse an image already used as another page hero or the homepage hero unless no other suitable image exists.',
      },
    },
    'galleryhero.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(GalleryHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryHero;
